import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Ticket, Clock, AlertCircle, MessageSquare, User, Send,
  X, Building2, ChevronRight, Loader2, Camera, Sparkles,
  ExternalLink, Copy, Download, Image, Zap, RefreshCw,
  CheckCircle, XCircle, AlertTriangle, Eye, CreditCard,
  Briefcase, Plane, Coffee, Wine, Heart, FileText
} from 'lucide-react';
import { adminAPI } from '../../api';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';

// Moment type configurations
const MOMENT_ICONS = {
  creative_workday: Briefcase,
  escape: Plane,
  unwind: Coffee,
  after_hours: Wine,
  gentle_celebration: Heart
};

const MOMENT_LABELS = {
  creative_workday: 'Creative Workday',
  escape: 'The Escape',
  unwind: 'Unwind',
  after_hours: 'After Hours',
  gentle_celebration: 'Gentle Celebration'
};

const AdminTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loadingTicket, setLoadingTicket] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [imageViewer, setImageViewer] = useState({ open: false, url: '', title: '' });

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await adminAPI.getTickets();
      setTickets(response.data.tickets || []);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTicket = async (ticketId) => {
    setLoadingTicket(true);
    try {
      const response = await adminAPI.getTicket(ticketId);
      const ticket = response.data;
      // Parse messages if string
      if (typeof ticket.messages === 'string') {
        ticket.messages = JSON.parse(ticket.messages);
      }
      setSelectedTicket(ticket);
    } catch (error) {
      toast.error('Failed to load ticket details');
    } finally {
      setLoadingTicket(false);
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      await adminAPI.updateTicket(ticketId, { status: newStatus });
      toast.success('Ticket status updated');
      fetchTickets();
      if (selectedTicket?.ticket_id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;
    
    setSubmitting(true);
    try {
      await adminAPI.updateTicket(selectedTicket.ticket_id, { message: replyText });
      toast.success('Reply sent');
      setReplyText('');
      // Refresh ticket to see new message
      handleViewTicket(selectedTicket.ticket_id);
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-700';
      case 'in_progress': return 'bg-amber-100 text-amber-700';
      case 'resolved': return 'bg-green-100 text-green-700';
      case 'closed': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-orange-100 text-orange-700';
      case 'medium': return 'bg-blue-100 text-blue-700';
      case 'low': return 'bg-slate-100 text-slate-600';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'output_quality': 'Output Quality',
      'credit_issue': 'Credit Issue',
      'api_error': 'API Error',
      'policy_issue': 'Policy Issue',
      'other': 'Other',
      'general': 'General'
    };
    return labels[category] || category || 'General';
  };

  const getCategoryColor = (category) => {
    const colors = {
      'output_quality': 'bg-purple-100 text-purple-700',
      'credit_issue': 'bg-amber-100 text-amber-700',
      'api_error': 'bg-red-100 text-red-700',
      'policy_issue': 'bg-orange-100 text-orange-700',
      'other': 'bg-slate-100 text-slate-700',
      'general': 'bg-slate-100 text-slate-700'
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  // Filter tickets
  let filteredTickets = tickets;
  if (filterStatus) {
    filteredTickets = filteredTickets.filter(t => t.status === filterStatus);
  }
  if (filterCategory) {
    filteredTickets = filteredTickets.filter(t => t.category === filterCategory);
  }

  const openCount = tickets.filter(t => t.status === 'open').length;
  const inProgressCount = tickets.filter(t => t.status === 'in_progress').length;
  const operationTickets = tickets.filter(t => t.operation_id).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Operation Details Card Component
  const OperationDetailsCard = ({ ticket }) => {
    const opData = ticket.full_operation_data || ticket.operation_details;
    if (!opData && !ticket.operation_id) return null;

    const MomentIcon = opData?.moment_type ? MOMENT_ICONS[opData.moment_type] : null;

    return (
      <div className="mt-4 border border-amber-200 bg-amber-50 rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-amber-100 border-b border-amber-200 flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-600" />
          <span className="font-medium text-amber-800">Linked Operation Details</span>
          {opData?.api_type && (
            <span className={`ml-auto px-2 py-0.5 rounded text-xs font-medium ${
              opData.api_type === 'tryon' ? 'bg-blue-500 text-white' : 'bg-purple-500 text-white'
            }`}>
              {opData.api_type === 'tryon' ? 'TryOn' : 'Moments'}
            </span>
          )}
        </div>
        
        <div className="p-4 space-y-4">
          {/* Images Section */}
          <div className="grid grid-cols-3 gap-3">
            {/* Output Image */}
            {opData?.output_image_url && (
              <div className="space-y-1">
                <p className="text-xs text-slate-500 font-medium">Output</p>
                <div 
                  className="aspect-square bg-white rounded-lg overflow-hidden border border-slate-200 cursor-pointer group relative"
                  onClick={() => setImageViewer({ open: true, url: opData.output_image_url, title: 'Output Image' })}
                >
                  <img src={opData.output_image_url} alt="Output" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Eye className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            )}
            
            {/* Human Image (TryOn) */}
            {opData?.human_image_url && (
              <div className="space-y-1">
                <p className="text-xs text-slate-500 font-medium">Human Input</p>
                <div 
                  className="aspect-square bg-white rounded-lg overflow-hidden border border-slate-200 cursor-pointer group relative"
                  onClick={() => setImageViewer({ open: true, url: opData.human_image_url, title: 'Human Input Image' })}
                >
                  <img src={opData.human_image_url} alt="Human" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Eye className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            )}
            
            {/* Garment Image (TryOn) */}
            {opData?.garment_image_url && (
              <div className="space-y-1">
                <p className="text-xs text-slate-500 font-medium">Garment Input</p>
                <div 
                  className="aspect-square bg-white rounded-lg overflow-hidden border border-slate-200 cursor-pointer group relative"
                  onClick={() => setImageViewer({ open: true, url: opData.garment_image_url, title: 'Garment Input Image' })}
                >
                  <img src={opData.garment_image_url} alt="Garment" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Eye className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Operation Metadata */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            {/* Operation ID */}
            <div className="bg-white p-2 rounded-lg border border-slate-200">
              <p className="text-xs text-slate-400">Operation ID</p>
              <div className="flex items-center gap-1 mt-0.5">
                <code className="text-xs font-mono truncate">{opData?.request_id || ticket.operation_id}</code>
                <button 
                  onClick={() => copyToClipboard(opData?.request_id || ticket.operation_id, 'Operation ID')}
                  className="text-slate-400 hover:text-slate-600 flex-shrink-0"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Credits */}
            <div className="bg-white p-2 rounded-lg border border-slate-200">
              <p className="text-xs text-slate-400">Credits Debited</p>
              <p className="font-semibold text-slate-900 flex items-center gap-1">
                <CreditCard className="w-3 h-3 text-slate-400" />
                {opData?.credits_consumed || 0}
              </p>
            </div>

            {/* Cache Status */}
            <div className="bg-white p-2 rounded-lg border border-slate-200">
              <p className="text-xs text-slate-400">Cache Status</p>
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                opData?.cached ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
              }`}>
                {opData?.cached ? <RefreshCw className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
                {opData?.cached ? 'Cached' : 'New'}
              </span>
            </div>

            {/* SKU */}
            {opData?.sku_id && (
              <div className="bg-white p-2 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-400">SKU ID</p>
                <p className="font-medium text-slate-700 truncate">{opData.sku_id}</p>
              </div>
            )}

            {/* Customer */}
            {opData?.customer_id && (
              <div className="bg-white p-2 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-400">Customer ID</p>
                <p className="font-medium text-slate-700 truncate">{opData.customer_id}</p>
              </div>
            )}

            {/* Operation Status */}
            <div className="bg-white p-2 rounded-lg border border-slate-200">
              <p className="text-xs text-slate-400">Op Status</p>
              <span className={`inline-flex items-center gap-1 text-xs font-medium ${
                opData?.status === 'success' ? 'text-emerald-600' :
                opData?.status === 'failed' ? 'text-red-600' : 'text-slate-600'
              }`}>
                {opData?.status === 'success' ? <CheckCircle className="w-3 h-3" /> :
                 opData?.status === 'failed' ? <XCircle className="w-3 h-3" /> :
                 <Clock className="w-3 h-3" />}
                {opData?.status || 'Unknown'}
              </span>
            </div>

            {/* Moment Type (for Moments) */}
            {opData?.moment_type && (
              <div className="bg-white p-2 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-400">Moment Type</p>
                <p className="font-medium text-purple-700 flex items-center gap-1">
                  {MomentIcon && <MomentIcon className="w-3 h-3" />}
                  {MOMENT_LABELS[opData.moment_type] || opData.moment_type}
                </p>
              </div>
            )}

            {/* Latency */}
            {opData?.latency_ms && (
              <div className="bg-white p-2 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-400">Latency</p>
                <p className="font-medium text-slate-700">{opData.latency_ms}ms</p>
              </div>
            )}

            {/* Timestamp */}
            {opData?.created_at && (
              <div className="bg-white p-2 rounded-lg border border-slate-200">
                <p className="text-xs text-slate-400">Operation Time</p>
                <p className="font-medium text-slate-700 text-xs">
                  {format(parseISO(opData.created_at), 'MMM d, HH:mm')}
                </p>
              </div>
            )}
          </div>

          {/* Cache Key */}
          {opData?.cache_key && (
            <div className="bg-white p-2 rounded-lg border border-slate-200">
              <p className="text-xs text-slate-400 mb-1">Cache Key</p>
              <div className="flex items-center gap-2">
                <code className="text-xs font-mono text-slate-600 truncate flex-1">{opData.cache_key}</code>
                <button 
                  onClick={() => copyToClipboard(opData.cache_key, 'Cache Key')}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          {/* Output URL */}
          {opData?.output_image_url && (
            <div className="bg-white p-2 rounded-lg border border-slate-200">
              <p className="text-xs text-slate-400 mb-1">Output CDN URL</p>
              <div className="flex items-center gap-2">
                <code className="text-xs font-mono text-slate-600 truncate flex-1">{opData.output_image_url}</code>
                <button 
                  onClick={() => copyToClipboard(opData.output_image_url, 'URL')}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <Copy className="w-3 h-3" />
                </button>
                <a 
                  href={opData.output_image_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-600"
                >
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          )}

          {/* Error Message */}
          {opData?.error_message && (
            <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
              <p className="text-xs text-red-600 font-medium flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Error Message
              </p>
              <p className="text-sm text-red-700 mt-1">{opData.error_message}</p>
            </div>
          )}

          {/* Custom Prompt (for Moments) */}
          {opData?.custom_prompt && (
            <div className="bg-purple-50 border border-purple-200 p-3 rounded-lg">
              <p className="text-xs text-purple-600 font-medium">Custom Prompt Used</p>
              <p className="text-sm text-purple-700 mt-1">{opData.custom_prompt}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="admin-tickets-page">
      {/* Page Title */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Support Tickets</h1>
        <p className="text-sm text-slate-500">Manage partner support requests and view operation details</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <p className="text-3xl font-bold text-slate-900">{tickets.length}</p>
          <p className="text-sm text-slate-500">Total</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <p className="text-3xl font-bold text-blue-600">{openCount}</p>
          <p className="text-sm text-slate-500">Open</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <p className="text-3xl font-bold text-amber-600">{inProgressCount}</p>
          <p className="text-sm text-slate-500">In Progress</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <p className="text-3xl font-bold text-green-600">{tickets.filter(t => t.status === 'resolved').length}</p>
          <p className="text-sm text-slate-500">Resolved</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
          <p className="text-3xl font-bold text-purple-600">{operationTickets}</p>
          <p className="text-sm text-slate-500">With Operations</p>
        </div>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-slate-200 p-4"
      >
        <div className="flex flex-wrap gap-4">
          {/* Status Filter */}
          <div>
            <p className="text-xs text-slate-500 mb-2">Status</p>
            <div className="flex flex-wrap gap-2">
              {[
                { value: '', label: 'All' },
                { value: 'open', label: 'Open' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'resolved', label: 'Resolved' },
                { value: 'closed', label: 'Closed' }
              ].map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setFilterStatus(filter.value)}
                  className={`px-3 py-1.5 rounded-lg transition-colors text-sm font-medium ${
                    filterStatus === filter.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {/* Category Filter */}
          <div>
            <p className="text-xs text-slate-500 mb-2">Category</p>
            <div className="flex flex-wrap gap-2">
              {[
                { value: '', label: 'All' },
                { value: 'output_quality', label: 'Output Quality' },
                { value: 'credit_issue', label: 'Credit Issue' },
                { value: 'api_error', label: 'API Error' },
                { value: 'policy_issue', label: 'Policy Issue' }
              ].map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setFilterCategory(filter.value)}
                  className={`px-3 py-1.5 rounded-lg transition-colors text-sm font-medium ${
                    filterCategory === filter.value
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Tickets List */}
      <div className="space-y-4">
        {filteredTickets.length > 0 ? (
          filteredTickets.map((ticket, index) => (
            <motion.div
              key={ticket.ticket_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="bg-white rounded-xl border border-slate-200 p-6 hover:border-slate-300 transition-colors cursor-pointer"
              onClick={() => handleViewTicket(ticket.ticket_id)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h3 className="font-semibold text-slate-900">{ticket.subject}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(ticket.status)}`}>
                      {ticket.status?.replace('_', ' ')}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(ticket.priority || ticket.severity)}`}>
                      {ticket.priority || ticket.severity || 'medium'}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(ticket.category)}`}>
                      {getCategoryLabel(ticket.category)}
                    </span>
                    {ticket.operation_id && (
                      <span className="px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700 flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        Operation Linked
                      </span>
                    )}
                  </div>
                  <p className="text-slate-500 text-sm mb-3 line-clamp-2">{ticket.description}</p>
                  <div className="flex items-center gap-4 text-sm text-slate-400 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Building2 className="w-4 h-4" />
                      {ticket.business_name || 'Unknown'}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {ticket.partner_email || ticket.contact_name || 'Unknown'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {ticket.created_at ? format(parseISO(ticket.created_at), 'MMM d, HH:mm') : '-'}
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
              </div>
            </motion.div>
          ))
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Ticket className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No tickets found</h3>
            <p className="text-slate-500">
              {filterStatus || filterCategory ? 'Try adjusting your filters' : 'No support tickets yet'}
            </p>
          </div>
        )}
      </div>

      {/* Ticket Detail Modal */}
      <AnimatePresence>
        {selectedTicket && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="p-6 border-b border-slate-200 flex-shrink-0">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">{selectedTicket.subject}</h3>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(selectedTicket.status)}`}>
                        {selectedTicket.status?.replace('_', ' ')}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(selectedTicket.priority || selectedTicket.severity)}`}>
                        {selectedTicket.priority || selectedTicket.severity || 'medium'}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getCategoryColor(selectedTicket.category)}`}>
                        {getCategoryLabel(selectedTicket.category)}
                      </span>
                      <span className="text-xs text-slate-500">
                        {selectedTicket.created_at ? format(parseISO(selectedTicket.created_at), 'MMM d, yyyy HH:mm') : '-'}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => setSelectedTicket(null)} className="text-slate-400 hover:text-slate-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Partner Info */}
                <div className="mt-4 p-3 bg-slate-50 rounded-lg flex items-center gap-4 text-sm flex-wrap">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">{selectedTicket.business_name || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">{selectedTicket.contact_name || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">{selectedTicket.partner_email || 'N/A'}</span>
                  </div>
                </div>

                {/* Status Update Buttons */}
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-slate-500">Update Status:</span>
                  {['open', 'in_progress', 'resolved', 'closed'].map((status) => (
                    <button
                      key={status}
                      onClick={() => handleUpdateStatus(selectedTicket.ticket_id, status)}
                      disabled={selectedTicket.status === status}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize ${
                        selectedTicket.status === status
                          ? 'bg-blue-100 text-blue-700 cursor-not-allowed'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {status.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {loadingTicket ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* Operation Details (if linked) */}
                    <OperationDetailsCard ticket={selectedTicket} />

                    {/* Messages */}
                    <div className="space-y-4">
                      <h4 className="font-medium text-slate-700 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Conversation
                      </h4>
                      {selectedTicket.messages?.map((msg, index) => (
                        <div
                          key={index}
                          className={`p-4 rounded-lg ${
                            msg.sender === 'partner' ? 'bg-slate-50 mr-8' : 'bg-blue-50 ml-8'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-sm font-medium ${
                              msg.sender === 'partner' ? 'text-slate-700' : 'text-blue-700'
                            }`}>
                              {msg.sender === 'partner' ? 'Partner' : 'Support Team'}
                            </span>
                            <span className="text-xs text-slate-400">
                              {msg.timestamp ? format(parseISO(msg.timestamp), 'MMM d, HH:mm') : '-'}
                            </span>
                          </div>
                          <p className="text-slate-700 text-sm whitespace-pre-wrap">{msg.message}</p>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Reply Input */}
              {selectedTicket.status !== 'closed' && (
                <div className="p-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      placeholder="Type your reply..."
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleReply()}
                      data-testid="ticket-reply-input"
                    />
                    <button
                      onClick={handleReply}
                      disabled={submitting || !replyText.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                      data-testid="send-reply-btn"
                    >
                      {submitting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <><Send className="w-4 h-4" /> Reply</>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Image Viewer Modal */}
      <AnimatePresence>
        {imageViewer.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center p-4"
            onClick={() => setImageViewer({ open: false, url: '', title: '' })}
          >
            <button
              onClick={() => setImageViewer({ open: false, url: '', title: '' })}
              className="absolute top-4 right-4 p-2 text-white/80 hover:text-white"
            >
              <X className="w-8 h-8" />
            </button>
            <div className="text-center" onClick={(e) => e.stopPropagation()}>
              <p className="text-white/80 mb-4 text-sm">{imageViewer.title}</p>
              <img
                src={imageViewer.url}
                alt={imageViewer.title}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
              <div className="mt-4 flex items-center justify-center gap-4">
                <a
                  href={imageViewer.url}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download
                </a>
                <button
                  onClick={() => copyToClipboard(imageViewer.url, 'URL')}
                  className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy URL
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminTickets;
